import { MAX_PHOTOS } from '../../constants';
import { v4 as uuidv4 } from 'uuid';

import { HorizontalContainer, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import DropZone from 'components/DropZone';

import useTranslation from 'pages/MaintenancePage/hooks/useTranslation';

import ServicePhotoThumb from './components/ServicePhotoThumb';
import { ACCEPT_FILE_TYPES, TEST_ID } from './constants';
import type { FilesSelectedHandler, FunctionComponent, Props, ServiceRecordPhoto } from './types';

import styles from './styles.module.scss';

const ServicePhotosField: FunctionComponent<Props> = ({
    className = '',
    onAddPhotos,
    onRemovePhoto,
    photos,
    testId = TEST_ID
}) => {
    const t = useTranslation();

    // Prototype-only: each photo becomes an in-memory object URL that is never
    // persisted. useServiceDraft revokes the URL when a photo is removed or its draft
    // is discarded; URLs that reach a saved record stay alive for history thumbnails.
    // The real implementation uploads the file via the signed-URL pattern and stores
    // the returned asset URL instead.
    const handleFilesSelected: FilesSelectedHandler = ({ files }) => {
        const remainingSlots = Math.max(MAX_PHOTOS - photos.length, 0);
        const selectedPhotos: ServiceRecordPhoto[] = files.slice(0, remainingSlots).map(file => ({
            fileName: file.name,
            id: uuidv4(),
            url: URL.createObjectURL(file)
        }));

        if (selectedPhotos.length === 0) {
            return;
        }

        onAddPhotos({ photos: selectedPhotos });
    };

    return (
        <VerticalContainer className={`${styles.servicePhotosField} ${className}`} testId={testId}>
            <Wrapper className={styles.label}>{t.LOG_SERVICE_PHOTOS}</Wrapper>

            <DropZone
                acceptFileTypes={ACCEPT_FILE_TYPES}
                ctaText={t.LOG_SERVICE_PHOTOS_CTA}
                formatsText={t.LOG_SERVICE_PHOTOS_FORMATS}
                isProcessing={false}
                onFilesSelected={handleFilesSelected}
            />

            {photos.length > 0 && (
                <HorizontalContainer className={styles.thumbnails}>
                    {photos.map(photo => (
                        <ServicePhotoThumb key={photo.id} onRemove={onRemovePhoto} photo={photo} />
                    ))}
                </HorizontalContainer>
            )}

            <Wrapper className={styles.count}>
                {t.LOG_SERVICE_PHOTO_COUNT({ count: photos.length, max: MAX_PHOTOS })}
            </Wrapper>
        </VerticalContainer>
    );
};

export default ServicePhotosField;
